import type {
  ChurnRiskLevel,
  UsageScopeSummary,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import type { UsageAreaDayRow } from "@/v2-backoffice-client/usage/usage.utils"
import type {
  PortfolioTimeframeKey,
  PortfolioWindow,
} from "@/v2-backoffice-client/portfolio/portfolio.filters"
import {
  deriveTrialPulse,
  type PortfolioCustomerSummary,
} from "@/v2-backoffice-client/portfolio/portfolio.utils"
import {
  derivePortfolioMovers,
  derivePortfolioRevenueAtRisk,
  type PortfolioRiskCounts,
} from "@/v2-backoffice-client/portfolio/portfolio.health.utils"
import { derivePortfolioAudience } from "@/v2-backoffice-client/portfolio/portfolio.audience.utils"
import PortfolioActivityHero from "@/v2-backoffice-client/portfolio/PortfolioActivityHero"
import PortfolioKpiCards from "@/v2-backoffice-client/portfolio/PortfolioKpiCards"
import PortfolioRiskBreakdown from "@/v2-backoffice-client/portfolio/PortfolioRiskBreakdown"
import PortfolioAudience from "@/v2-backoffice-client/portfolio/PortfolioAudience"
import PortfolioMovers from "@/v2-backoffice-client/portfolio/PortfolioMovers"
import PortfolioRenewals from "@/v2-backoffice-client/portfolio/PortfolioRenewals"

export type PortfolioOverviewSectionProps = {
  /** The globally filtered book — every KPI reads the same set. */
  customers: PortfolioCustomerSummary[]
  /** The book rolled up as one scope (shared with the Health tab). */
  fleet: UsageScopeSummary
  /** Fleet daily rows for the activity hero (unfiltered by app). */
  areaDays: UsageAreaDayRow[]
  timeWindow: PortfolioWindow
  timeframeKey: PortfolioTimeframeKey
  onTimeframeChange(key: PortfolioTimeframeKey): void
  /** Churn donut counts — facet-narrowed except for the churn facet itself. */
  riskCounts: PortfolioRiskCounts
  activeChurnLevels: ChurnRiskLevel[]
  onChurnLevelToggle(level: ChurnRiskLevel): void
}

// The Overview sub-tab: the portfolio's pulse — KPI strip, daily activity
// vs churn breakdown, audience rhythm, and the renewals/movers pair.
function PortfolioOverviewSection(props: PortfolioOverviewSectionProps) {
  const {
    customers,
    fleet,
    areaDays,
    timeWindow,
    timeframeKey,
    onTimeframeChange,
    riskCounts,
    activeChurnLevels,
    onChurnLevelToggle,
  } = props

  // From the fully filtered set — the churn facet must narrow this KPI too.
  const atRiskCount = customers.filter(
    (customer) =>
      customer.summary.churnRisk.level === "high" ||
      customer.summary.churnRisk.level === "medium",
  ).length
  const trialPulse = deriveTrialPulse(customers)
  // The NOK behind the at-risk count — same fully filtered set, so the two
  // KPIs always describe the same accounts.
  const revenueAtRisk = derivePortfolioRevenueAtRisk(customers)
  // Derived once: the KPI row shows the size numbers, the rhythm section
  // the day-by-day shape — same object, so they can never disagree.
  const audience = derivePortfolioAudience(customers)
  // Full list — the movers card previews the top few, the dialogs show all.
  const movers = derivePortfolioMovers(customers, Number.POSITIVE_INFINITY)

  return (
    <div className="usage-report">
      <PortfolioKpiCards
        customerCount={customers.length}
        atRiskCount={atRiskCount}
        revenueAtRisk={revenueAtRisk}
        trials={trialPulse}
        fleet={fleet}
        audience={audience}
      />

      {/* No fixed grid height: the chart's own modest height sizes the row,
          and the churn column stretches to match it — the two sections start
          (same header row) and end together. */}
      <div className="grid gap-x-14 gap-y-9 xl:grid-cols-3">
        <PortfolioActivityHero
          className="xl:col-span-2"
          areaDays={areaDays}
          timeWindow={timeWindow}
          timeframeKey={timeframeKey}
          onTimeframeChange={onTimeframeChange}
        />
        <PortfolioRiskBreakdown
          counts={riskCounts}
          currency={revenueAtRisk.currency}
          activeLevels={activeChurnLevels}
          onLevelToggle={onChurnLevelToggle}
        />
      </div>

      {/* The audience SIZE lives in the KPI strip; this adds the shape —
          the DAU strip and return frequency. */}
      <PortfolioAudience audience={audience} />

      {/* Each full width: the renewals rows carry five signals and need the
          room to stay on one line; the movers' two lists then sit side by
          side in their own grid. */}
      <PortfolioRenewals customers={customers} />
      <PortfolioMovers movers={movers} />

      <p className="text-xs text-muted-foreground">
        Customer-linked seats only (internal and unassigned users are excluded),
        action-weighted like every usage view: exports, reports and saves count
        more than clicks. Computed daily from console tracking events, Console
        v1 and v2 alike.
      </p>
    </div>
  )
}

export default PortfolioOverviewSection
